"use client";

import { toPng } from "html-to-image";

/** Turns a community name into a safe PNG file name. */
const sanitizeFileName = (name: string) =>
  name
    .trim()
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase() || "community";

/**
 * 1x1 transparent PNG. Passed to `html-to-image` as the `imagePlaceholder` so
 * that when an image (e.g. a cross-origin parish logo the CDN won't serve with
 * CORS headers) can't be fetched, the capture falls back to this instead of an
 * empty `src` — an empty `src` fires the cloned img's `error` event, which
 * `html-to-image` turns into a rejection that would kill the whole export.
 */
const TRANSPARENT_PIXEL =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==";

type DownloadShareCardOptions = {
  /** The share asset card DOM node to export (QR + logo + text + wordmark). */
  node: HTMLElement;
  /** File name (without extension); sanitized before use. */
  fileName?: string;
};

/**
 * Fetches an image URL and resolves it to a data URL, or `null` if it can't be
 * read (network error, or a CDN that doesn't allow cross-origin reads). Used to
 * pre-inline the parish logo so `html-to-image` embeds it cleanly without a
 * cross-origin fetch of its own.
 */
const fetchImageAsDataUrl = async (url: string): Promise<string | null> => {
  try {
    const response = await fetch(url, { mode: "cors" });
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () =>
        resolve(typeof reader.result === "string" ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

/**
 * Pre-inlines every non-data-URL `<img>` inside `node` as a data URL, mutating
 * the live nodes in place, and returns a function that restores the original
 * `src`/`srcset`/`crossorigin` attributes. Images that can't be fetched are
 * left as-is (the `imagePlaceholder` fallback handles those during capture).
 * This gives the logo the best chance of appearing in the export while never
 * blocking it.
 *
 * `srcset`/`sizes` are stripped alongside the swap: `next/image` renders the
 * centre logo with a `srcset`, and once `src` is a data URL `html-to-image`
 * skips its own inlining (it only clears `srcset` when it does the swap). A
 * leftover `srcset` would let the sandboxed capture pick an external candidate
 * that can't load, blanking the logo — so we drop it and restore it after.
 */
const inlineImages = async (node: HTMLElement): Promise<() => void> => {
  const images = Array.from(node.querySelectorAll("img"));
  const restores: Array<() => void> = [];

  await Promise.all(
    images.map(async (img) => {
      const src = img.getAttribute("src");
      if (!src || src.startsWith("data:")) return;

      const dataUrl = await fetchImageAsDataUrl(src);
      if (!dataUrl) return;

      const originalCrossOrigin = img.getAttribute("crossorigin");
      const originalSrcset = img.getAttribute("srcset");
      const originalSizes = img.getAttribute("sizes");
      img.setAttribute("src", dataUrl);
      img.removeAttribute("crossorigin");
      img.removeAttribute("srcset");
      img.removeAttribute("sizes");

      restores.push(() => {
        img.setAttribute("src", src);
        if (originalCrossOrigin !== null)
          img.setAttribute("crossorigin", originalCrossOrigin);
        if (originalSrcset !== null) img.setAttribute("srcset", originalSrcset);
        if (originalSizes !== null) img.setAttribute("sizes", originalSizes);
      });
    }),
  );

  return () => restores.forEach((restore) => restore());
};

/**
 * Renders the full share asset card DOM node to a PNG and triggers a browser
 * download — capturing everything the user sees in the modal (the QR with the
 * centred parish logo, community name, location, member count, and the Hallow
 * wordmark).
 *
 * Robustness: the parish logo is a Hallow CDN image loaded through the
 * same-origin `next/image` optimizer (see `CommunityInviteQrCode`), so it is
 * readable by the canvas capture. It is pre-inlined as a data URL when
 * reachable so it appears in the export; when it isn't, the capture still
 * succeeds with everything else (QR + text) — `imagePlaceholder` +
 * `onImageErrorHandler` stop a single unfetchable image from rejecting the
 * whole export. Any real failure is logged and rethrown so the caller can
 * surface it rather than fail silently.
 */
export const downloadShareCard = async ({
  node,
  fileName = "community",
}: DownloadShareCardOptions): Promise<void> => {
  const restoreImages = await inlineImages(node);

  try {
    const dataUrl = await toPng(node, {
      cacheBust: true,
      pixelRatio: 2,
      // If an image still can't be embedded, fall back to a transparent pixel
      // and swallow its load error so the rest of the card still exports.
      imagePlaceholder: TRANSPARENT_PIXEL,
      onImageErrorHandler: () => {},
    });

    const anchor = document.createElement("a");
    anchor.href = dataUrl;
    anchor.download = `${sanitizeFileName(fileName)}.png`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } catch (error) {
    console.error("Failed to export community share card", error);
    throw error;
  } finally {
    restoreImages();
  }
};
